/** A fixed optical origin in the selected GLTF avatar's bind chart, in metres.
 * It deliberately never follows the animated head, hips, or a retained pose.
 * Valoria's imported skin inverse binds and bind-space vertices already include
 * authored source-frame conversions (or the explicit ZoeSourceFrame); applying
 * node.matrixWorld here would apply
 * the current player/SSX transform (and sometimes that conversion) twice.
 */
export function createFirstPersonEyeAnchor({THREE: T}) {
  const cache = new WeakMap();
  const finite = matrix => matrix?.elements?.every(Number.isFinite) && Math.abs(matrix.determinant()) > 1e-15;
  const valid = point => point && [point.x, point.y, point.z].every(Number.isFinite) && Math.abs(point.x) < .5 && point.y > .5 && point.y < 3 && Math.abs(point.z) < .6;
  const opticalName = name => /(?:^|[_\s])eyes?(?:$|[_\d]|fitted|white|iris|highlight)/i.test(name ?? '');
  function inspect(body) {
    const meshes = [], binds = new Map();
    // Zoe intentionally retains SSX centimetre/Z-up inverse binds and geometry
    // under one immutable conversion node. The anime GLTFs already bake their
    // source conversion into their inverse binds, so do not apply other parents.
    const frame = body.getObjectByName('ZoeSourceFrame'), chart = new T.Matrix4();
    if (frame) {
      const chain = []; for (let node = frame; node && node !== body; node = node.parent) chain.push(node);
      for (const node of chain.reverse()) chart.multiply(node.matrixAutoUpdate ? new T.Matrix4().compose(node.position, node.quaternion, node.scale) : node.matrix);
    }
    body.traverse(node => {
      if (!node.isMesh) return;
      meshes.push(node);
      node.skeleton?.bones.forEach((bone, index) => {
        const inverse = node.skeleton.boneInverses[index];
        if (finite(inverse)) binds.set(bone, chart.clone().multiply(inverse.clone().invert()));
      });
    });
    const named = (...names) => [...binds.keys()].find(bone => names.includes(bone.name.toLowerCase()));
    const point = bone => bone && binds.has(bone) ? new T.Vector3().setFromMatrixPosition(binds.get(bone)) : null;
    const left = point(named('j_adj_l_faceeye', 'lefteye', 'eye_l', 'eyeleft'));
    const right = point(named('j_adj_r_faceeye', 'righteye', 'eye_r', 'eyeright'));
    const eye = left && right ? left.add(right).multiplyScalar(.5) : null;
    const headBone = named('j_bip_c_head', 'head'), head = point(headBone);
    const eyeBounds = new T.Box3(), headSlice = new T.Box3(), vertex = new T.Vector3();
    const headBones = new Set(); headBone?.traverse(node => { if (node.isBone) headBones.add(node); });
    const fallbackHeight = valid(head) ? head.y + .06 : null;
    for (const mesh of meshes) {
      const geometry = mesh.geometry, position = geometry?.getAttribute('position');
      // The selected native assets use skinned optical geometry. Rigid eye
      // meshes without inverse binds cannot provide a pose-independent chart.
      if (!position || !mesh.isSkinnedMesh || !finite(mesh.bindMatrix)) continue;
      const material = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const optical = new Set(material.flatMap((item, index) => opticalName(item?.name) ? [index] : []));
      const wholeEye = opticalName(mesh.name), index = geometry.index;
      const count = index?.count ?? position.count, groups = geometry.groups.length ? geometry.groups : [{start: 0, count, materialIndex: 0}];
      const rangeStart = Math.max(0, geometry.drawRange.start), rangeEnd = Math.min(count, rangeStart + geometry.drawRange.count);
      for (const group of groups) {
        if (!wholeEye && !optical.has(group.materialIndex)) continue;
        const end = Math.min(count, group.start + group.count, rangeEnd);
        for (let i = Math.max(group.start, rangeStart); i < end; i++) {
          vertex.fromBufferAttribute(position, index ? index.getX(i) : i).applyMatrix4(mesh.bindMatrix).applyMatrix4(chart);
          if (valid(vertex)) eyeBounds.expandByPoint(vertex);
        }
      }
      if (fallbackHeight === null || !headBones.size) continue;
      const skinIndex = geometry.getAttribute('skinIndex'), skinWeight = geometry.getAttribute('skinWeight');
      if (!skinIndex || !skinWeight) continue;
      const headIndices = new Set(mesh.skeleton.bones.flatMap((bone, index) => headBones.has(bone) ? [index] : []));
      for (let i = 0; i < position.count; i++) {
        let weight = 0;
        for (let slot = 0; slot < Math.min(4, skinIndex.itemSize, skinWeight.itemSize); slot++) if (headIndices.has(skinIndex.getComponent(i, slot))) weight += skinWeight.getComponent(i, slot);
        if (weight < .5) continue;
        vertex.fromBufferAttribute(position, i).applyMatrix4(mesh.bindMatrix).applyMatrix4(chart);
        // A narrow, central slice estimates the visible front of the face when
        // the rig has neither separate eyeballs nor paired eye joints (Zoe).
        // Hair tips/ears and points far above/below the optical band do not set it.
        if (valid(vertex) && Math.abs(vertex.y - fallbackHeight) <= .035 && Math.abs(vertex.x - head.x) <= .055) headSlice.expandByPoint(vertex);
      }
    }
    if (!eyeBounds.isEmpty()) {
      const origin = valid(eye) ? eye : eyeBounds.getCenter(new T.Vector3());
      // Eye joints are often behind the iris surface. The actual optical front
      // keeps the camera over the face, rather than over the neck opening.
      origin.z = Math.max(origin.z, eyeBounds.max.z);
      if (valid(origin)) return {position: origin, source: valid(eye) ? 'eye-bind-and-surface' : 'eye-surface', calibrated: true};
    }
    if (valid(eye)) return {position: eye, source: 'eye-bind', calibrated: true};
    if (fallbackHeight !== null) {
      const origin = new T.Vector3(head.x, fallbackHeight, headSlice.isEmpty() ? head.z : Math.max(head.z, headSlice.max.z));
      if (valid(origin)) return {position: origin, source: headSlice.isEmpty() ? 'head-bind' : 'head-bind-and-surface', calibrated: true};
    }
    return {position: new T.Vector3(0, 1.65, 0), source: 'fallback', calibrated: false};
  }
  return {
    calibratedEye({avatarRoot, avatarBody = avatarRoot, preset} = {}) {
      if (!avatarBody?.isObject3D) return {position: new T.Vector3(0, 1.65, 0), source: 'fallback', calibrated: false};
      const revision = avatarRoot?.userData?.avatarModelRevision;
      let record = cache.get(avatarBody);
      if (!record || record.revision !== revision || record.preset !== preset || record.children.length !== avatarBody.children.length || record.children.some((node, index) => node !== avatarBody.children[index])) {
        record = {...inspect(avatarBody), revision, preset, children: avatarBody.children.slice()}; cache.set(avatarBody, record);
      }
      return {position: record.position.clone(), source: record.source, calibrated: record.calibrated};
    },
  };
}
